import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, InputLabel, Link } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { CheckBoxCircleFillIcon, CloseIcon, UploadIcon } from '~/assets/icons';
import { IMasterDataItem } from '~/utils/interface/master';
import utilityApi from '~/apis/utility';
import { handleApiError, notify, readExcel } from '~/utils/helper';
import BaseRow from '~/components/common/base-row/BaseRow';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import { useFormik } from 'formik';
import { IImportEquipment } from '~/utils/interface/equipment';
import equipmentApi from '~/apis/equipment';
import { useAppDispatch } from '~/redux/hooks';
import { commonActions } from '~/thunks/common/commonSlice';
import styles from './ImportEquipmentDialog.module.scss';
import classNames from 'classnames/bind';

type ImportEquipemntDialogProps = {
  open: boolean;
  setOpen?: (open: boolean, needRefreshData?: boolean) => void;
};

const initialValues: IImportEquipment = {
  agent: '',
};

const cx = classNames.bind(styles);

function ImportEquipmentDialog(props: ImportEquipemntDialogProps) {
  const { t } = useTranslation(['admin/equipment']);
  const dispatch = useAppDispatch();
  const { getRootProps, getInputProps, isDragAccept } = useDropzone({
    maxFiles: 1,
    onDrop: (acceptedFiles: Array<File>) => {
      setUploadFile(acceptedFiles[0]);
    },
  });
  const [agentOptions, setAgentOptions] = useState<IMasterDataItem[]>([]);
  const [uploadFile, setUploadFile] = useState<File>();
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const form = useFormik({
    validateOnChange: false,
    initialValues,
    onSubmit: async (values) => {
      if (!uploadFile) {
        return handleApiError(t('importEquipmentDialog.fileRequired'));
      }

      try {
        dispatch(commonActions.setIsLoading(true));
        let data = await readExcel(uploadFile);

        if (!data.length) return handleApiError(t('importEquipmentDialog.dataEmpty'));

        if (values.agent) {
          data = data.map((item) => ({
            ...item,
            agent: values.agent,
          }));
        }

        await equipmentApi.importDevice(data);

        notify('success', t('importEquipmentDialog.importSuccess'));
        handleSetOpen(false, true);
      } catch (e: any) {
        handleApiError(e);
      } finally {
        dispatch(commonActions.setIsLoading(false));
      }
    },
  });

  useEffect(() => {
    (async () => {
      try {
        const response = await utilityApi.getListAgents();
        setAgentOptions(
          response.data.map((item) => {
            return {
              id: item.username,
              name: item.username + ' - ' + item.name,
              data0: item.name,
            };
          })
        );
      } catch (error) {
        handleApiError(error);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //#endregion Implement Hook

  //#region Handle Function
  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    form.resetForm();
    setUploadFile(undefined);
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  useEffect(() => {
    if (props.open) {
      document.addEventListener('dragenter', handleDragenter);
      document.addEventListener('drop', handleDrop);
    } else {
      document.removeEventListener('dragenter', handleDragenter);
      document.removeEventListener('drop', handleDrop);
      setIsDragging(false);
    }

    return () => {
      document.removeEventListener('dragenter', handleDragenter);
      document.removeEventListener('drop', handleDrop);
    };
  }, [props.open]);

  const handleDragenter = (e: any) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDrop = (e: any) => {
    e.preventDefault();
    setIsDragging(false);
  };
  //#endregion Handle Function
  return (
    <Dialog open={props.open} onClose={() => handleSetOpen(false)} fullWidth maxWidth='sm'>
      <DialogTitle className={cx({ 'is-dragging-mode': isDragging })}>
        <Box>{t('importEquipmentDialog.title')}</Box>
        <IconButton onClick={() => handleSetOpen(false)}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={cx({ 'is-dragging-mode': isDragging })}>
        <Box className='flex gap-[30px] items-center mb-[10px] !opacity-100'>
          <Box {...getRootProps()}>
            <Box component='input' {...getInputProps()} />
            <Box
              className={`flex w-[216px] items-center justify-center rounded-[8px] p-[7px] border cursor-pointer
              ${isDragging ? 'border-[#005F73] bg-[#D8F5FB]' : 'border border-[#C6C6C6]'}
              ${isDragAccept ? 'text-[#005F73] font-bold' : ''}
              `}
            >
              <UploadIcon className={`w-[20px] h-[20px] mr-auto ${isDragAccept ? '[&>*]:fill-[#005F73]' : ''}`} />
              <Box className='mr-auto'>{t('importEquipmentDialog.selectFile')}</Box>
            </Box>
          </Box>

          {uploadFile ? (
            <Box className='flex gap-[10px]'>
              <CheckBoxCircleFillIcon className='w-[17px] h-[17px]' />
              <Box>{uploadFile.name}</Box>
            </Box>
          ) : null}
        </Box>
        <Box className='text-[11px] w-[216px] text-center mb-[10px]'>{t('importEquipmentDialog.dragGuide')}</Box>
        <BaseRow className='gap-[56px] mb-[10px] !items-center'>
          <InputLabel>{t('importEquipmentDialog.agent')}:</InputLabel>
          <Box className='w-[235px]'>
            <BaseSelect
              size='medium'
              options={agentOptions}
              value={form.values.agent}
              placeholder={t('select')}
              name='agent'
              isSearchEnable
              onChange={form.handleChange}
              error={Boolean(form.errors?.agent)}
              helperText={form.errors?.agent}
              displayInvalidValue
            />
          </Box>
        </BaseRow>
        <BaseRow className='gap-[38px] mb-[10px]'>
          <InputLabel>{t('importEquipmentDialog.templateFile')}:</InputLabel>
          <Box className='w-1/2'>
            <Link href='files/DeviceImportModel_Sample.xlsx' target='_blank'>
              {t('importEquipmentDialog.downloadTemplateFile')}
            </Link>
          </Box>
        </BaseRow>
        <Box
          className='leading-[150%] mb-[10px]'
          dangerouslySetInnerHTML={{ __html: t('importEquipmentDialog.guide') }}
        />
        <Button
          variant='contained'
          color='secondary'
          className='min-w-[125px] flex ml-auto'
          onClick={() => form.handleSubmit()}
        >
          {t('importEquipmentDialog.import')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
export default memo(ImportEquipmentDialog);
